/**
 * What a view is grouped by.
 *
 * Read as one sentence down the rows, the same way the sort menu is: the first
 * term is the grouping and each one after it divides the groups the ones before
 * it made. That is also the order the backend applies them in, and the order
 * the columns are drawn in beside the rows.
 *
 * Grouping *is* ordering, as far as the rows are concerned -- the server orders
 * by the group by fields before it applies the view's own sorts -- so the
 * direction control here is the same one the sort menu has, and it means the
 * same thing.
 */
import { GripVertical, Group, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Field, ViewGroupBy, ViewOf } from '@/api/types'
import { canGroupByInView } from '../fields/behavior'
import { Button } from '@/shared/ui/button'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { verticalListSortingStrategy } from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableList } from '@/shared/components/sortable'
import { useSortableItem } from '@/shared/hooks/use-sortable-item'
import { useViewGroupBys } from './group-by-mutations'
import type { SortDirection } from './sort-mutations'

export function GroupByMenu({
  tableId,
  view,
  fields,
}: {
  tableId: number
  view: ViewOf<'grid'>
  fields: readonly Field[]
}) {
  const { t } = useTranslation()
  const groupBys = view.group_bys ?? []
  const { add, change, remove, prioritize } = useViewGroupBys(tableId, view.id)

  // A field a view cannot be grouped by is not offered. That is a stricter
  // question than sorting -- a file column sorts by its filenames and a uuid
  // sorts perfectly well, and a heading made of either is a heading over one
  // row -- so it asks the field's own behaviour rather than reusing the sort's.
  const groupable = fields.filter((field) => canGroupByInView(field))
  const used = new Set(groupBys.map((groupBy) => groupBy.field))
  const free = groupable.filter((field) => !used.has(field.id))

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5">
          <Group className="size-4 opacity-70" />
          {groupBys.length === 0
            ? t('view.group')
            : t('view.groupCount', { count: groupBys.length })}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-2">
        {groupBys.length === 0 ? (
          <p className="px-2 py-1.5 text-sm text-muted-foreground">
            {t('view.groupNone')}
          </p>
        ) : (
          <ul aria-label={t('view.group')} className="flex flex-col gap-1">
            <SortableList
              ids={groupBys.map((groupBy) => groupBy.id)}
              strategy={verticalListSortingStrategy}
              modifiers={[restrictToVerticalAxis]}
              onReorder={prioritize}
            >
              {groupBys.map((groupBy, index) => (
                <GroupByRow
                  key={groupBy.id}
                  groupBy={groupBy}
                  index={index}
                  fields={fields}
                  free={free}
                  onChange={change}
                  onRemove={remove}
                />
              ))}
            </SortableList>
          </ul>
        )}

        <div className="mt-2 border-t pt-2">
          {free.length === 0 ? (
            <p className="px-2 py-1 text-xs text-muted-foreground">
              {t('view.groupNoFields')}
            </p>
          ) : (
            <NativeSelect
              size="sm"
              className="w-full"
              aria-label={t('view.groupAdd')}
              // Never keeps what was chosen: choosing is the whole action, and
              // the row it creates is where the choice then lives.
              value=""
              onChange={(event) => {
                add(Number(event.target.value), 'ASC')
              }}
            >
              <NativeSelectOption value="" disabled>
                {t('view.groupAdd')}
              </NativeSelectOption>
              {free.map((field) => (
                <NativeSelectOption key={field.id} value={field.id}>
                  {field.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * One level of grouping, and the handle that moves it.
 *
 * A component of its own because the drag hook is a hook, and the handle is a
 * control of its own because the row is already two selects and a button --
 * Space inside one of those must not pick the row up.
 */
function GroupByRow({
  groupBy,
  index,
  fields,
  free,
  onChange,
  onRemove,
}: {
  groupBy: ViewGroupBy
  index: number
  fields: readonly Field[]
  /** The fields not already grouped by, which is what may be chosen instead. */
  free: readonly Field[]
  onChange: (
    groupById: number,
    changes: { field?: number; order?: SortDirection },
  ) => void
  onRemove: (groupById: number) => void
}) {
  const { t } = useTranslation()
  const { setNodeRef, style, handleProps, isDragging } = useSortableItem(groupBy.id)

  // The term's own field is offered even when it is not in `free`: a formula
  // whose expression changed can stop being groupable while a view is still
  // grouped by it, and a select with no option for its own value shows the
  // wrong field.
  const own = fields.find((field) => field.id === groupBy.field)

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 ${isDragging ? 'opacity-60' : ''}`}
    >
      <button
        type="button"
        aria-label={t('view.groupMove', { field: own?.name ?? '' })}
        className="cursor-grab rounded p-0.5 text-muted-foreground hover:bg-muted"
        {...handleProps}
      >
        <GripVertical className="size-4" />
      </button>
      {/* Which level this is, which is where it sits in the list -- so it is
          also the only thing on the row that says a drag landed. */}
      <span className="w-14 shrink-0 text-xs text-muted-foreground">
        {index === 0 ? t('view.groupBy') : t('view.thenBy')}
      </span>
      <NativeSelect
        size="sm"
        className="flex-1"
        aria-label={t('view.groupField', { position: index + 1 })}
        value={groupBy.field}
        onChange={(event) => {
          onChange(groupBy.id, { field: Number(event.target.value) })
        }}
      >
        {(own === undefined ? free : [own, ...free]).map((field) => (
          <NativeSelectOption key={field.id} value={field.id}>
            {field.name}
          </NativeSelectOption>
        ))}
      </NativeSelect>
      <NativeSelect
        size="sm"
        aria-label={t('view.groupDirection', { field: own?.name ?? '' })}
        value={groupBy.order ?? 'ASC'}
        onChange={(event) => {
          onChange(groupBy.id, { order: event.target.value as SortDirection })
        }}
      >
        <NativeSelectOption value="ASC">{t('view.ascending')}</NativeSelectOption>
        <NativeSelectOption value="DESC">{t('view.descending')}</NativeSelectOption>
      </NativeSelect>
      <button
        type="button"
        aria-label={t('view.groupRemove', { field: own?.name ?? '' })}
        onClick={() => {
          onRemove(groupBy.id)
        }}
        className="rounded p-1 text-muted-foreground hover:bg-muted"
      >
        <X className="size-3.5" />
      </button>
    </li>
  )
}
